import type { OrderItem, Sku } from '@lombok/shared';
import { addToDraft, type DraftItem } from './orderDraft';

/**
 * "Repetir pedido": turn the lines of a past order into the customer's order in progress. Only lines
 * whose SKU can still be ordered today are loaded; the rest are listed so the customer knows what was
 * left out. Prices are not copied: the create screen shows each SKU's current price.
 */

interface CatalogEntry {
  productId: string;
  name: string;
  active: boolean;
}

/** Lower case, no accents, single spaces: names written by hand still match. */
export function normalizeName(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Which SKUs of the order can be ordered now, mapped to their product. A line stores its product id
 * (orders created from now on); older orders only have the product name, so those are matched by name.
 * A SKU counts when its product is active in the catalog and the SKU itself is active.
 */
export async function findOrderable(
  lines: readonly OrderItem[],
  catalog: readonly CatalogEntry[],
  loadSkus: (productId: string) => Promise<readonly Pick<Sku, 'skuId' | 'active'>[]>,
): Promise<Map<string, string>> {
  const activeProducts = catalog.filter((p) => p.active);
  const orderable = new Map<string, string>();
  for (const line of lines) {
    if (orderable.has(line.skuId)) continue;
    const candidates = line.productId
      ? activeProducts.filter((p) => p.productId === line.productId)
      : activeProducts.filter((p) => normalizeName(p.name) === normalizeName(line.productName));
    for (const product of candidates) {
      const skus = await loadSkus(product.productId);
      if (skus.some((s) => s.skuId === line.skuId && s.active)) {
        orderable.set(line.skuId, product.productId);
        break;
      }
    }
  }
  return orderable;
}

export interface ReorderPlan {
  items: DraftItem[];
  /** "Product name (SKU code)" of the lines that cannot be repeated. */
  skipped: string[];
}

export function planReorder(lines: readonly OrderItem[], orderable: ReadonlyMap<string, string>): ReorderPlan {
  let items: DraftItem[] = [];
  const skipped: string[] = [];
  for (const line of lines) {
    const productId = orderable.get(line.skuId);
    if (productId) {
      items = addToDraft(items, { productId, skuId: line.skuId, quantity: line.quantity });
    } else {
      const label = `${line.productName} (${line.skuCode})`;
      if (!skipped.includes(label)) skipped.push(label);
    }
  }
  return { items, skipped };
}
